import { clsx } from 'clsx'
import { apiClient } from '@/api/client'
import { useAccount } from '@/api/hooks/use-account'
import { useBillingUsage } from '@/api/hooks/use-billing'
import { formatNumber } from '@/lib/format'
import { OverageBanner } from '@/pages/billing-overage-banner'

const TRANSACTIONAL_LIMITS: Record<string, number> = {
  free: 10_000,
  starter: 25_000,
  pro: 75_000,
  growth: 300_000,
  scale: 2_000_000,
}

const MARKETING_LIMITS: Record<string, number> = {
  free: 3_000,
  starter: 10_000,
  pro: 50_000,
  growth: 150_000,
  scale: 500_000,
}

const TIER_PRICING: Record<string, Record<string, number>> = {
  transactional: { free: 0, starter: 5, pro: 18, growth: 45, scale: 349 },
  marketing: { free: 0, starter: 15, pro: 59, growth: 149, scale: 399 },
}

function usagePercent(sent: number, limit: number): number {
  if (limit <= 0) return 0
  return Math.min((sent / limit) * 100, 100)
}

function barColor(percent: number): string {
  if (percent >= 90) return 'bg-danger'
  if (percent >= 70) return 'bg-warning'
  return 'bg-brand-500'
}

function UsageBar({
  label,
  sent,
  limit,
}: {
  label: string
  sent: number
  limit: number
}): React.JSX.Element {
  const pct = usagePercent(sent, limit)
  return (
    <div>
      <div className="flex justify-between text-sm">
        <span className="text-text-secondary">{label}</span>
        <span className="font-medium text-text-primary">
          {formatNumber(sent)} / {formatNumber(limit)}
        </span>
      </div>
      <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-surface-2">
        <div
          className={clsx('h-full rounded-full transition-all duration-500', barColor(pct))}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }): React.JSX.Element {
  const colorClass =
    status === 'active' || status === 'trialing'
      ? 'bg-green-100 text-green-700'
      : status === 'canceled'
        ? 'bg-red-100 text-red-700'
        : 'bg-gray-100 text-gray-700'
  return (
    <span className={clsx('rounded-full px-2.5 py-0.5 text-xs font-medium capitalize', colorClass)}>
      {status}
    </span>
  )
}

async function openBillingPortal(): Promise<void> {
  const result = await apiClient.post<{ portal_url: string }>('/dashboard/billing/portal', {})
  window.location.href = result.portal_url
}

interface SubscriptionCardProps {
  category: 'transactional' | 'marketing'
  tier: string
  subscriptionStatus: string | null
  emailsSent: number
  emailLimit: number
  contactCount?: number
  contactLimit?: number
}

function SubscriptionCard({
  category,
  tier,
  subscriptionStatus,
  emailsSent,
  emailLimit,
  contactCount,
  contactLimit,
}: SubscriptionCardProps): React.JSX.Element {
  const price = TIER_PRICING[category]?.[tier] ?? 0
  const hasActiveSub = subscriptionStatus === 'active' || subscriptionStatus === 'trialing'
  const isFree = tier === 'free'
  const label = category === 'transactional' ? 'Transactional' : 'Marketing'
  const emailLabel = category === 'transactional' ? 'API Emails' : 'Campaign Emails'

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">{label}</h2>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm capitalize text-text-secondary">{tier}</span>
            <span className="text-sm text-text-tertiary">&mdash;</span>
            <span className="text-sm font-medium text-text-primary">
              {price === 0 ? 'Free' : `$${price}/mo`}
            </span>
            {subscriptionStatus && <StatusBadge status={subscriptionStatus} />}
            {!subscriptionStatus && <StatusBadge status="free" />}
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        <UsageBar label={emailLabel} sent={emailsSent} limit={emailLimit} />
        {contactCount !== undefined && contactLimit !== undefined && (
          <UsageBar label="Contacts" sent={contactCount} limit={contactLimit} />
        )}
      </div>

      <div className="mt-5 flex items-center gap-3">
        <a
          href={`/choose-plan?category=${category}`}
          className={clsx(
            'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
            isFree
              ? 'bg-brand-500 text-white hover:bg-brand-600'
              : 'border border-surface-3 bg-surface-0 text-text-primary hover:bg-surface-1',
          )}
        >
          {isFree ? 'Upgrade' : 'Change plan'}
        </a>
        {hasActiveSub && (
          <button
            type="button"
            onClick={() => {
              openBillingPortal()
            }}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-1"
          >
            Manage in Polar &rarr;
          </button>
        )}
      </div>
    </div>
  )
}

export function BillingPage(): React.JSX.Element {
  const { data, isLoading } = useAccount()
  const { data: usage } = useBillingUsage()

  if (isLoading) {
    return <div className="py-12 text-center text-text-tertiary">Loading billing...</div>
  }

  if (!data) {
    return (
      <div className="py-12 text-center text-text-tertiary">
        Unable to load billing information.
      </div>
    )
  }

  const { account } = data
  const txTier = usage?.tier ?? account.tier
  const mktTier = usage?.marketing.tier ?? account.marketing_tier ?? 'free'
  const txLimit = usage?.emails.included ?? TRANSACTIONAL_LIMITS[txTier] ?? 10_000
  const txSent = usage?.emails.sent ?? account.emails_sent_this_period
  const mktLimit = MARKETING_LIMITS[mktTier] ?? 3_000
  const mktSent = usage?.marketing.sent ?? account.marketing_emails_sent_this_period ?? 0
  const contactLimit = Math.max(txLimit, mktLimit)

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-semibold text-text-primary">Billing</h1>

      <OverageBanner />

      <SubscriptionCard
        category="transactional"
        tier={txTier}
        subscriptionStatus={usage?.subscription_status ?? account.polar_subscription_status}
        emailsSent={txSent}
        emailLimit={txLimit}
        contactCount={account.contact_count ?? 0}
        contactLimit={contactLimit}
      />

      <SubscriptionCard
        category="marketing"
        tier={mktTier}
        subscriptionStatus={null}
        emailsSent={mktSent}
        emailLimit={mktLimit}
      />
    </div>
  )
}
